import {
  invalidateAdminCompanyTreeQueryData,
  type PostApiAdminSyncTreeRequestBody,
  useGetCompanyById,
  usePostApiAdminCompanyTreeSettingsClearSyncFields,
  usePatchApiAdminCompany,
} from '@sit/client-shared';
import {
  enumToArray,
  ForceFullSyncObject,
  type ForceFullSyncObjectType,
} from '@sit/core';
import { noop, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import { CardColumn, CardRow } from 'carbon-react/lib/components/card';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Divider from 'carbon-react/lib/components/divider';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import Typography from 'carbon-react/lib/components/typography';
import { formatDistanceToNow } from 'date-fns';
import { type ChangeEvent, useState } from 'react';
import { Link } from 'react-router';
import { FeatureFlag } from '../../../components/FeatureFlag';
import {
  companySyncFieldOptions,
  type CompanySyncField,
} from '../../../helpers/company-sync-fields';
import { send } from '../../../helpers/requests';
import toastr from '../../../helpers/toastr';
import { useSyncStatus } from '../../SyncStatus/useSyncStatus';
import { isSyncEnabled } from '../helpers';

const resourceNames = {
  ALLOCATION_TEMPLATE: 'Allocation templates',
  APPROVAL: 'Approvals',
  CLASS: 'Classes',
  CONTRACT: 'Contracts',
  COSTTYPE: 'Cost types',
  CUSTOMER: 'Customers',
  DEPARTMENT: 'Departments',
  EMPLOYEEPOSITION: 'Employee positions',
  FIXEDASSET: 'Fixed assets',
  ITEM: 'Items',
  LABORCLASS: 'Labor classes',
  LABORSHIFT: 'Labor shifts',
  LABORUNION: 'Labor unions',
  LOCATION: 'Locations',
  PROJECT: 'Projects',
  PROJECTRESOURCE: 'Project resources',
  PROJECTSTATUS: 'Project statuses',
  TASK: 'Tasks',
  TASKRESOURCE: 'Task resources',
  TIMESHEET: 'Timesheets',
  TIMESHEETENTRY: 'Timesheet entries',
  TIMETYPE: 'Time types',
  VENDOR: 'Vendors',
  WAREHOUSE: 'Warehouses',
} as const;

const forcedSyncValues = enumToArray(ForceFullSyncObject).sort();

export interface SyncStatusFormProps {
  companyTreeSettingsId: number;
  intacctCompanyId: string;
  dataSyncActive: boolean;
}

function SyncStatusForm({
  companyTreeSettingsId,
  intacctCompanyId,
  dataSyncActive,
}: SyncStatusFormProps) {
  const [state, setState] = useState<{
    refetchData: boolean;
    requestError: string | null;
    syncError: string | null;
    syncStart: Date | null;
    syncEnd: Date | null;
    selectAllForceSyncObjects: boolean;
    forceSyncObjects: ForceFullSyncObjectType[];
  }>({
    refetchData: false,
    requestError: null,
    syncError: null,
    syncStart: null,
    syncEnd: null,
    selectAllForceSyncObjects: false,
    forceSyncObjects: [],
  });

  const { data: company } = useGetCompanyById({ companyTreeSettingsId });

  const [requestError, setRequestError] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [clearSyncFields, setClearSyncFields] = useState<CompanySyncField[]>([
    'lastSettingsSync',
  ]);
  const queryClient = useQueryClient();

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    try {
      setRequestError(null);
      setLoading(true);
      const data = { intacctCompanyId };
      await send({
        method: 'POST',
        url: '/admin/release-hung-sync',
        data,
        credentials: 'include',
      });
      toastr.success('Sync job released successfully');
    } catch (error) {
      setRequestError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefetchAllDataChange = (e: ChangeEvent<HTMLInputElement>) => {
    const refetchData = e.target.checked;
    setState((prevState) => ({
      ...prevState,
      refetchData,
    }));
  };

  const handleSelectAllForceSyncObjectsChange = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectAllForceSyncObjects = e.target.checked;
    const forceSyncObjects = selectAllForceSyncObjects
      ? (enumToArray(ForceFullSyncObject) as ForceFullSyncObjectType[])
      : [];
    setState((prevState) => ({
      ...prevState,
      selectAllForceSyncObjects,
      forceSyncObjects,
    }));
  };

  const getForceFullSyncObjectChangeFunction =
    (obj: ForceFullSyncObjectType) => (e: ChangeEvent<HTMLInputElement>) => {
      const objectChecked = e.target.checked;
      setState((prevState) => ({
        ...prevState,
        forceSyncObjects: objectChecked
          ? [...prevState.forceSyncObjects, obj]
          : prevState.forceSyncObjects.filter((o) => o !== obj),
      }));
    };

  const manualSync = async () => {
    try {
      const data: PostApiAdminSyncTreeRequestBody = {
        companyTreeSettingsId,
        refetchData: state.refetchData,
      };
      await send({
        method: 'POST',
        url: '/admin/sync-tree',
        data,
        credentials: 'include',
      });
      setState((prevState) => ({
        ...prevState,
        requestError: null,
        companyTreeSettingsId,
      }));
      toastr.success('Sync job submitted');
    } catch (error: unknown) {
      setState((prevState) => ({
        ...prevState,
        requestError: (error as Error).message,
        requestSuccess: false,
      }));
    }
  };

  const mutation = usePatchApiAdminCompany();
  const clearSyncFieldsMutation =
    usePostApiAdminCompanyTreeSettingsClearSyncFields();

  const updateCompany = async () => {
    await mutation.mutateAsync(
      {
        body: {
          forceFullSync: state.forceSyncObjects,
        },
        pathParams: {
          id: `${companyTreeSettingsId}`,
        },
      },
      {
        onSuccess: () => {
          toastr.success('Company updated');
        },
        onError: (error) => {
          toastr.error(
            error instanceof Error ? error.message : (error as string),
          );
        },
      },
    );
  };

  const clearSelectedSyncFields = async () => {
    try {
      await clearSyncFieldsMutation.mutateAsync({
        body: {
          companyTreeSettingsIds: [companyTreeSettingsId],
          fields: clearSyncFields,
        },
      });
      await invalidateAdminCompanyTreeQueryData(queryClient);
      toastr.success(
        `Cleared ${clearSyncFields.length} sync ${clearSyncFields.length === 1 ? 'field' : 'fields'}.`,
      );
    } catch (error) {
      toastr.error(
        error instanceof Error ? error.message : 'Failed to clear sync fields',
      );
    }
  };

  const { data: syncStatus, progress } = useSyncStatus(
    companyTreeSettingsId,
    (error) => {
      setState((prevState) => ({
        ...prevState,
        requestError: error,
      }));
    },
  );

  const atLeastOneEntityIsSyncEnabled = company?.entities.some((entity) =>
    isSyncEnabled(entity),
  );

  return (
    <>
      {syncStatus && (
        <div>
          <Checkbox
            readOnly
            label="Triggers enabled"
            checked={dataSyncActive}
            onChange={noop}
          />
          <Divider type="horizontal" variant="typical" />
          {!state.requestError &&
            syncStatus.syncStart &&
            !syncStatus.syncEnd && (
              <Message variant="info">
                Last sync started{' '}
                {formatDistanceToNow(new Date(syncStatus.syncStart), {
                  addSuffix: true,
                })}
              </Message>
            )}
          {!state.requestError && syncStatus.syncEnd && !(progress < 100) && (
            <Message variant="info">
              Synced successfully{' '}
              {formatDistanceToNow(new Date(syncStatus.syncEnd), {
                addSuffix: true,
              })}
            </Message>
          )}

          {progress < 100 && (
            <Message variant="info">Sync in progress {progress}%</Message>
          )}
        </div>
      )}
      {companyTreeSettingsId != null && (
        <div className="intacct-sync-status">
          <FeatureFlag featureName="ENABLE_ADMIN_REFETCH_ALL_DATA">
            <Divider type="horizontal" variant="typical" />
            <Checkbox
              label="Refetch all data"
              checked={state.refetchData}
              onChange={handleRefetchAllDataChange}
            />
          </FeatureFlag>
          <Box my={1}>
            {!atLeastOneEntityIsSyncEnabled && (
              <Message variant="warning">
                <div>
                  To sync, click{' '}
                  <code style={{ fontSize: '12px' }}>Re-enable sync</code> on at
                  least one entity in the{' '}
                  <Link to={'entities'}>entities page</Link>.
                </div>
              </Message>
            )}
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Box mr={2}>
              <Button
                type="submit"
                onClick={handleSubmit}
                variantType="secondary"
              >
                {loading ? (
                  <Loader
                    loaderType="ring"
                    variant="inline"
                    size="small"
                    loaderLabel="Releasing..."
                  />
                ) : (
                  'Release hung sync job'
                )}
              </Button>
            </Box>
            <Box>
              <Button
                onClick={manualSync}
                disabled={
                  !atLeastOneEntityIsSyncEnabled ||
                  (progress ? progress < 100 : false)
                }
              >
                Sync now
              </Button>
            </Box>
          </Box>
        </div>
      )}
      {(state.requestError || requestError) && (
        <Message variant="error">{state.requestError || requestError}</Message>
      )}
      <Divider type="horizontal" variant="typical" />
      <Typography variant="h1" mb={1}>
        Clear sync fields
      </Typography>
      <Typography>
        Clear one or more sync timestamps to force the next scheduled sync to
        re-fetch cached Intacct data.
      </Typography>
      {companySyncFieldOptions.map((option) => (
        <Checkbox
          key={option.value}
          label={`${option.label} — ${option.description}`}
          checked={clearSyncFields.includes(option.value)}
          onChange={(e) => {
            const checked = e.target.checked;
            setClearSyncFields((current) =>
              checked
                ? [...current, option.value]
                : current.filter((field) => field !== option.value),
            );
          }}
        />
      ))}
      <CardRow>
        <CardColumn align="right">
          <Button
            onClick={clearSelectedSyncFields}
            variantType="secondary"
            disabled={
              clearSyncFields.length === 0 || clearSyncFieldsMutation.isPending
            }
          >
            Clear selected sync fields
          </Button>
        </CardColumn>
      </CardRow>
      <Divider type="horizontal" variant="typical" />
      <Typography variant="h1" mb={1}>
        Force Full Sync
      </Typography>
      <Typography>
        A full sync will be forced on the following objects during the next sync
      </Typography>
      <Checkbox
        label="Select all"
        checked={state.selectAllForceSyncObjects}
        onChange={handleSelectAllForceSyncObjectsChange}
      />
      {forcedSyncValues.map((obj) => (
        <Checkbox
          key={obj}
          label={
            obj in resourceNames
              ? resourceNames[obj as keyof typeof resourceNames]
              : obj
          }
          checked={state.forceSyncObjects.includes(
            obj as ForceFullSyncObjectType,
          )}
          onChange={getForceFullSyncObjectChangeFunction(
            obj as ForceFullSyncObjectType,
          )}
        />
      ))}

      <CardRow>
        <CardColumn align="right">
          <Button onClick={updateCompany} variantType="secondary">
            Save forced sync objects
          </Button>
        </CardColumn>
      </CardRow>
    </>
  );
}

export default SyncStatusForm;
